import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { NoteCard } from './NoteCard';
import { Note } from '../../types/note';
import { Plus, StickyNote } from 'lucide-react';

interface NotesGridProps {
    notes: Note[];
    onEdit: (note: Note) => void;
    onDelete: (id: number) => void;
    onAdd: () => void;
    /** Open the full-view reader (large notes) */
    onOpen?: (note: Note) => void;
}

/**
 * Notes list with virtualization: only the rows near the viewport are
 * mounted, so hundreds of notes scroll at 60fps. Columns per breakpoint are
 * mirrored from the previous static grid (1 / 2 / 3).
 */
export const NotesGrid: React.FC<NotesGridProps> = ({
    notes,
    onEdit,
    onDelete,
    onAdd,
    onOpen,
}) => {
    const parentRef = useRef<HTMLDivElement>(null);

    // column count by viewport width (matches the old md:grid-cols-2 lg:grid-cols-3)
    const useColumns = () => {
        const [cols, setCols] = React.useState(1);
        React.useEffect(() => {
            const mq2 = window.matchMedia('(min-width: 768px)');
            const mq3 = window.matchMedia('(min-width: 1024px)');
            const update = () => setCols(mq3.matches ? 3 : mq2.matches ? 2 : 1);
            update();
            mq2.addEventListener('change', update);
            mq3.addEventListener('change', update);
            return () => {
                mq2.removeEventListener('change', update);
                mq3.removeEventListener('change', update);
            };
        }, []);
        return cols;
    };
    const columns = useColumns();

    const rowCount = Math.ceil(notes.length / columns);
    const virtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 220, // card height estimate; measured after mount
        overscan: 4,
    });

    return (
        <div className="space-y-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <StickyNote className="text-blue-500" />
                    Notes ({notes.length})
                </h2>
                <button
                    onClick={onAdd}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-sm active:scale-95 min-h-[44px]"
                >
                    <Plus size={20} />
                    <span>Add Note</span>
                </button>
            </div>

            {notes.length === 0 ? (
                <div className="text-center py-20 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-800">
                    <div className="bg-blue-100 dark:bg-blue-900/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <StickyNote className="text-blue-600 dark:text-blue-400" size={32} />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No notes yet</h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-xs mx-auto mb-6">
                        Keep track of links and information with secure notes.
                    </p>
                    <button
                        onClick={onAdd}
                        className="text-blue-600 dark:text-blue-400 font-medium hover:underline"
                    >
                        Create your first note
                    </button>
                </div>
            ) : (
                <div
                    ref={parentRef}
                    className="overflow-y-auto"
                    style={{ contain: 'strict', height: 'calc(100dvh - 220px)', minHeight: '50dvh' }}
                >
                    <div
                        style={{
                            height: `${virtualizer.getTotalSize()}px`,
                            width: '100%',
                            position: 'relative',
                        }}
                    >
                        {virtualizer.getVirtualItems().map((virtualRow) => {
                            const rowNotes = notes.slice(
                                virtualRow.index * columns,
                                virtualRow.index * columns + columns,
                            );
                            return (
                                <div
                                    key={virtualRow.key}
                                    className="absolute top-0 left-0 w-full"
                                    style={{
                                        transform: `translateY(${virtualRow.start}px)`,
                                        display: 'grid',
                                        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                                        gap: '1rem',
                                        paddingBottom: '1rem',
                                    }}
                                >
                                    {rowNotes.map((note) => (
                                        <NoteCard
                                            key={note.id}
                                            note={note}
                                            onEdit={onEdit}
                                            onDelete={onDelete}
                                            onOpen={onOpen}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
